import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { 
  Loader2, 
  CheckCircle2, 
  AlertCircle, 
  Clock,
  Target,
  XCircle,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { getRun, getRunEvents, isTerminalStatus, type BackendRun, type BackendRunEvent } from "@/lib/reqqyBackend";
import { RunEventTimeline } from "@/components/run/RunEventTimeline";
import type { RunStatus } from "@/lib/types";

const POLL_INTERVAL = 2000;

export default function RunProgress() {
  const { runId } = useParams<{ runId: string }>();
  const navigate = useNavigate();

  const [run, setRun] = useState<BackendRun | null>(null);
  const [events, setEvents] = useState<BackendRunEvent[]>([]);
  const [error, setError] = useState<string | null>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const status: RunStatus = (run?.status as RunStatus) ?? "queued";
  const progressPct = run?.progress_pct ?? 0;
  const currentStep = run?.current_step ?? "Preparing…";

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const poll = useCallback(async () => {
    if (!runId) return;
    try {
      const [r, ev] = await Promise.all([getRun(runId), getRunEvents(runId)]);
      setRun(r);
      setEvents(ev);
      setError(null);

      if (isTerminalStatus(r.status)) {
        stopPolling();
        if (r.status === "succeeded") {
          setTimeout(() => navigate(`/dashboard/runs/${runId}/results`), 1500);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [runId, stopPolling, navigate]);

  useEffect(() => {
    poll(); // immediate first fetch
    pollingRef.current = setInterval(poll, POLL_INTERVAL);
    return stopPolling;
  }, [poll, stopPolling]);

  const statusConfig: Record<RunStatus, { icon: React.ElementType; label: string; color: string }> = {
    draft: { icon: Clock, label: "Draft", color: "text-muted-foreground" },
    queued: { icon: Clock, label: "Queued", color: "text-muted-foreground" },
    running: { icon: Loader2, label: "Analyzing", color: "text-primary" },
    succeeded: { icon: CheckCircle2, label: "Complete", color: "text-reqqy-success" },
    failed: { icon: XCircle, label: "Failed", color: "text-destructive" },
    cancelled: { icon: AlertCircle, label: "Cancelled", color: "text-muted-foreground" },
  };

  const StatusIcon = statusConfig[status].icon;

  return (
    <div className="min-h-screen p-6 md:p-8 lg:p-10">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <div className={cn(
            "w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6",
            status === "running" ? "gradient-primary glow-soft" : 
            status === "succeeded" ? "bg-reqqy-success/20" : "bg-muted"
          )}>
            <StatusIcon className={cn(
              "w-10 h-10",
              statusConfig[status].color,
              status === "running" && "animate-spin text-primary-foreground"
            )} />
          </div>
          <h1 className="text-2xl md:text-3xl font-display font-bold mb-2">
            {status === "succeeded" ? "Analysis Complete!" : 
             status === "failed" ? "Analysis Failed" : "Analyzing Your Requirements"}
          </h1>
          <p className="text-muted-foreground">
            {status === "succeeded" 
              ? "Your clarity report is ready. Redirecting..."
              : status === "failed"
              ? "Something went wrong during analysis."
              : currentStep}
          </p>
        </div>

        <Card className="glass mb-6">
          <CardContent className="p-8">
            <div className="space-y-6">
              <div className="text-center">
                <p className="text-lg font-medium mb-2">{currentStep}</p>
                <p className="text-sm text-muted-foreground">{progressPct}% complete</p>
              </div>

              <Progress value={progressPct} className="h-2" />
            </div>
          </CardContent>
        </Card>

        {/* Event timeline */}
        {events.length > 0 && (
          <Card className="glass mb-6">
            <CardContent className="p-6 max-h-48 overflow-auto">
              <RunEventTimeline events={events} />
            </CardContent>
          </Card>
        )}

        {/* Error display */}
        {(error || status === "failed") && (
          <Card className="glass mb-6 border-destructive/50">
            <CardContent className="p-6">
              <p className="text-sm text-destructive">
                {error || (run?.error_payload ? JSON.stringify(run.error_payload) : "Unknown error")}
              </p>
            </CardContent>
          </Card>
        )}

        <div className="flex items-center justify-center gap-4 mt-8">
          <Button 
            variant="outline" 
            onClick={() => navigate("/dashboard")}
          >
            Run in Background
          </Button>
          {status === "succeeded" && (
            <Button 
              onClick={() => navigate(`/dashboard/runs/${runId}/results`)}
              className="gradient-primary text-primary-foreground"
            >
              <Target className="w-4 h-4 mr-2" />
              View Results
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
